import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EpAdjsLocalChartService {

  constructor(private http: HttpClient) {}

  getCodeCombination(baseUrl: string, combinationKey: number): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_id', '9');
    urlParams.set('combination_key', combinationKey.toString());

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject?.[0] || null;
      })
    );
  }

  // Get accounts for EP Tax Adjustments
  getAccounts(
    baseUrl: string,
    params: {
      screen_key: number;
      ge_gecs_entity: string;
      source_type: string;
      search_string: string;
      acct_type_str: string;
      chart_key: number;
      parent_acct_key: number;
      system_acct_search: string;
      parent_system_acct_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'd1ndzz');
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get line details
  getLineDetails(
    baseUrl: string,
    screenKey: number,
    taxType: string,
    globalService: any
  ): Observable<any> {
    const actionCode = ['PTSP', 'BRPTP', 'BRPTSP', 'NCPTSP', 'NCPTSPNF'].includes(taxType) 
      ? 'r7pupz' : '6mnbxg';

    const urlParams = new URLSearchParams();
    urlParams.set('action_code', actionCode);
    urlParams.set('screen_key', screenKey.toString());

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get DCS line details
  getDCSLineDetails(
    baseUrl: string,
    postingAcct: string,
    templateId: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'p7ymkm');
    urlParams.set('posting_acct', postingAcct);
    urlParams.set('template_id', templateId);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get trading partners
  getTradingPartners(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    searchString: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', '50swfi');
    urlParams.set('tax_year', taxYear);
    urlParams.set('scenario', scenario);
    urlParams.set('search_string', searchString);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get projects and steps
  getProjectsAndSteps(
    baseUrl: string,
    taxYear: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'qbnyai');
    urlParams.set('tax_year', taxYear);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get DCS adjustment types
  getDcsAdjsTypes(): any[] {
    return [
      { ADJ_TYPE: 'PM', ADJ_DESC: 'Permanent' },
      { ADJ_TYPE: 'EQ', ADJ_DESC: 'Equity' },
      { ADJ_TYPE: 'TC', ADJ_DESC: 'Current Temporary' },
      { ADJ_TYPE: 'TN', ADJ_DESC: 'NonCurrent Temporary' },
      { ADJ_TYPE: 'TX', ADJ_DESC: 'Taxes' }
    ];
  }

  // Get Reportable Action types for DCS (static fallback)
  getReportableActionsStatic(): any[] {
    return [
      { CODE: 'DP', CODE_DESC: 'Decision Pending' },
      { CODE: 'ZBA', CODE_DESC: 'This Transaction has zero Book amount' },
      { CODE: 'BAA', CODE_DESC: 'Select Book Adj Accounts' }
    ];
  }

  // Load Reportable Action types from API
  loadReportableActions(
    baseUrl: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'w1ubsf');

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Save/Update adjustments (FIR)
  saveUpdateAdjustments(
    baseUrl: string,
    settings: any[],
    accounts: any[],
    globalService: any,
    filterParams: any,
    extraParams?: any
  ): Observable<any> {
    const jsonObj = JSON.stringify(accounts);
    const jsonSettings = JSON.stringify(settings);

    let params: any = {};
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams };
    }
    if (filterParams) {
      params = { ...params, ...filterParams };
    }
    if (extraParams) {
      params = { ...params, ...extraParams };
    }
    params.jsonObj = jsonObj;
    params.jsonSettings = jsonSettings;
    params.process_name = 'TAX_ADJS_LOCAL_ACCT';

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        const val = params[key];
        if (typeof val === 'object') {
          urlParams.set(key, JSON.stringify(val));
        } else {
          urlParams.set(key, val.toString());
        }
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=es4lx5',
      urlParams.toString(),
      { headers, withCredentials: true }
    );
  }

  // Save/Update adjustments (DCS)
  saveUpdateAdjustmentsDCS(
    baseUrl: string,
    settings: any[],
    accounts: any[],
    globalService: any,
    filterParams: any,
    extraParams?: any
  ): Observable<any> {
    const jsonObj = JSON.stringify(accounts);
    const jsonSettings = JSON.stringify(settings);

    let params: any = {};
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams };
    }
    if (filterParams) {
      params = { ...params, ...filterParams };
    }
    if (extraParams) {
      params = { ...params, ...extraParams };
    }
    params.jsonObj = jsonObj;
    params.jsonSettings = jsonSettings;
    params.process_name = 'TAX_ADJS_LOCAL_ACCT';

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        const value = (typeof params[key] === 'object')
          ? JSON.stringify(params[key])
          : params[key].toString();
        urlParams.set(key, value);
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=lmbn9a',
      urlParams.toString(),
      { headers, withCredentials: true }
    );
  }

  // Save/Update Book Reportable adjustments (DCS)
  saveUpdateAdjustmentsDCSBookReportable(
    baseUrl: string,
    settings: any[],
    accounts: any[],
    globalService: any,
    filterParams: any
  ): Observable<any> {
    const jsonObj = JSON.stringify(accounts);
    const jsonSettings = JSON.stringify(settings);

    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }
    params.jsonObj = jsonObj;
    params.jsonSettings = jsonSettings;
    params.process_name = 'TAX_ADJS_BOOK_REPORTABLE';

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=ea3aa3',
      urlParams.toString(),
      { headers, withCredentials: true }
    );
  }

  // Delete adjustment
  deleteAdjustment(
    baseUrl: string,
    data: any,
    globalService: any,
    filterParams: any
  ): Observable<any> {
    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }
    params = { ...data, ...params };

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=k2p5gw',
      urlParams.toString(),
      { headers, withCredentials: true }
    );
  }

  // Load existing adjustments - FIR
  loadExistingAdjustmentsFIR(
    baseUrl: string,
    params: {
      screen_key: number;
      combination_key: number;
      chart_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'q15pkg'); // FIR existing tax adjs action code
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load existing adjustments - DCS
  loadExistingAdjustmentsDCS(
    baseUrl: string,
    params: {
      screen_key: number;
      combination_key: number;
      chart_key: number;
      local_acct_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'z7xfyr'); // DCS existing tax adjs action code
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load DCS trigger data (Posting GL, Tax, M3 Form Line, Book Amt, etc.)
  loadDcsTriggerData(
    baseUrl: string,
    combinationKey: number,
    postingAcctKey: number,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'byc4kc');
    urlParams.set('combi_key', combinationKey.toString());
    urlParams.set('posting_acct_key', postingAcctKey.toString());

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load DCS template data
  loadTemplateData(
    baseUrl: string,
    chartKey: number,
    localAcctKey: number,
    systemAcctKey: number,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'uewdwv');
    urlParams.set('sys_acct', `${chartKey}_${localAcctKey}-${systemAcctKey}`);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load SchdM types
  loadSchdMTypes(
    baseUrl: string,
    chartKey: number,
    localAcctKey: number,
    systemAcctKey: number,
    templateId: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'jhsi7h');
    urlParams.set('sys_acct', `${chartKey}_${localAcctKey}-${systemAcctKey}`);
    urlParams.set('schdm_temp', templateId);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load TBBS accounts
  loadTbbsAccounts(
    baseUrl: string,
    chartKey: number,
    localAcctKey: number,
    systemAcctKey: number,
    templateId: string,
    schdmType: string,
    combinationKey: number,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', '26k8ua');
    urlParams.set('local_acct', localAcctKey.toString());
    urlParams.set('sys_acct', chartKey.toString());
    urlParams.set('schdm_temp', templateId);
    urlParams.set('schdm_type', schdmType);
    urlParams.set('source_system', 'D');
    urlParams.set('combination_key', combinationKey.toString());

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load G/L Accounts for Book Reportable
  loadGlAccounts(
    baseUrl: string,
    params: {
      tax_year: string;
      scenario: string;
      jcd_key: number;
      chart_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'aucc6o');
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load "top" GL accounts for the New Posting Acct (testFlag) variant dropdown.
  // Mirrors legacy getTopglAccts / getTopglAcctsB (action_code 'p88nfx').
  getTopGlAccounts(
    baseUrl: string,
    params: {
      tax_year: string;
      scenario: string;
      jcd_key: number;
      chart_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'p88nfx');

    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  loadReportableGlAccounts(
    baseUrl: string,
    chartKey: number,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'n6rrgu');

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        urlParams.set(key, globalService.globalParams[key]?.toString() || '');
      });
    }
    urlParams.set('chart_key', chartKey.toString());

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Legacy getreportableglAcctchangedata (action dpzi4u): CY / Earlier Adjustment amounts for a reportable G/L account.
  getReportableGlAcctChangeData(
    baseUrl: string,
    params: {
      cc_key: any;
      gl_acct_key: any;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'dpzi4u');

    if (globalService?.globalParams) {
      urlParams.set('tax_year', globalService.globalParams.tax_year?.toString() || '');
      urlParams.set('scenario', globalService.globalParams.scenario?.toString() || '');
      urlParams.set('jcd_key', globalService.globalParams.jcd_key?.toString() || '');
    }
    urlParams.set('cc_key', params.cc_key !== undefined && params.cc_key !== null ? params.cc_key.toString() : '');
    urlParams.set('gl_acct_key', params.gl_acct_key !== undefined && params.gl_acct_key !== null ? params.gl_acct_key.toString() : '');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load Reportable Transaction Types
  loadReportableTransTypes(
    baseUrl: string,
    params: {
      tax_year: string;
      scenario: string;
      jcd_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'hg67p4');
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load existing book reportable chart data (for Book Only Reportable tab)
  loadExistingBookReportable(
    baseUrl: string,
    params: {
      tax_year: string;
      scenario: string;
      jcd_key: number;
      cc_key: number;
      local_acct_key: number;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', '6lsis2');
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }
}
